import { z } from 'zod'
import { UTCMillisecondsSchema } from '../time'
import { TagIdSchema, type TagId } from '../tag'
import { FieldDataSchema, ResidentDataSchema, type ResidentData, type TinyResidentData } from '../resident'
import { NoteWithOwnerSchema, type NoteWithOwner } from '../notes'
import {
  AccountStatusIdSchema,
  CompanyIdSchema,
  CustomLocationIdSchema,
  LocationIdSchema,
  UserIdSchema,
  WorkflowActionIdSchema,
  WorkflowIdSchema,
  WorkflowStageIdSchema,
  type AccountStatusId,
  type LocationId,
  type TeamId,
  type UserId,
  type WorkflowActionId,
  type WorkflowStageId,
} from '../ids'
import { type TinyUser } from '../entity'
import { DispositionIdSchema, type DispositionId } from '../disposition'
import { CustomFieldFilterSchema, RangeFilterSchema } from '../customFieldMap'
import { AddressSchema, FirstAmericanStreetSchema, type TinyAddress } from '../address'
import { type TinyAccountContact } from '../accountContact'
import { PermitLabelSchema, PremiumLocationAttributesSchema } from './premium'
import { AltLatLngSchema, LatLngSchema, type LatLng, type LatLngBoundingBox } from './latLng'
import { GeoJSONMultiPolygonSchema, GeoJSONPolygonSchema } from './geojson'
import { PermitStatusSchema } from './firstAmerican'

const AttomSaleDataSchema = z.object({
  date: z.number().optional(),
  price: z.number().optional(),
  transactionId: z.number().optional(),
})
export type AttomSaleData = z.infer<typeof AttomSaleDataSchema>

const AttomValuationDataSchema = z.object({
  value: z.number().optional(),
  improvementsPercentage: z.number().optional(),
  landValue: z.number().optional(),
  improvementsValue: z.number().optional(),
})
export type AttomValuationData = z.infer<typeof AttomValuationDataSchema>

export const LocationAttributesSchema = z.object({
  ...PremiumLocationAttributesSchema.shape,
  occupantType: z.enum(['Renter', 'Owner']).optional(),
  pool: z.boolean().optional(),
  trust: z.boolean().optional(),
  company: z.boolean().optional(),
  parcelNumber: z.string().optional(),
  geoQuality: z.string().optional(),
  vestingRelation: z.string().optional(),
  neighborhoodCode: z.string().optional(),
  zonedCodeLocal: z.string().optional(),
  metropolitanDiv: z.string().optional(),
  minorCivilDiv: z.string().optional(),
  minorCivilDivName: z.string().optional(),
  minorCivilDivCode: z.string().optional(),
  yearBuilt: z.number().optional(),
  yearBuiltEffective: z.number().optional(),
  homeSize: z.number().optional(), // in SQFT
  lotSize: z.number().optional(),
  areaGross: z.number().optional(),
  areaLotDepth: z.number().optional(),
  areaLotWidth: z.number().optional(),
  parkingSpaces: z.number().optional(),
  lastOwnershipTransferDate: z.number().optional(),
  roofMaterial: z.string().optional(),
  roofConstruction: z.string().optional(),
  heatingFuel: z.string().optional(),
  foundation: z.string().optional(),
  lastUpdated: z.number().optional(),
  bathCount: z.number().optional(),
  bathPartialCount: z.number().optional(),
  bedroomCount: z.number().optional(),
  roomCount: z.number().optional(),
  storyCount: z.number().optional(),
  unitCount: z.number().optional(),
  buildingCount: z.number().optional(),
  sales: z
    .object({
      deed: AttomSaleDataSchema.optional(),
      latest: AttomSaleDataSchema.optional(),
      prior: AttomSaleDataSchema.optional(),
    })
    .optional(),
  valuations: z
    .object({
      market: AttomValuationDataSchema.optional(),
      assessed: AttomValuationDataSchema.optional(),
    })
    .optional(),
  taxExemptions: z.array(z.string()).optional(),
  utilities: z
    .object({
      securityAlarm: z.boolean().optional(),
      mobileHomeHookup: z.boolean().optional(),
    })
    .optional(),
})
export type LocationAttributes = z.infer<typeof LocationAttributesSchema>

const MetropolitanStatisticalAreaSchema = z.object({
  name: z.string().optional(),
  code: z.string().optional(),
})
export type MetropolitanStatisticalArea = z.infer<typeof MetropolitanStatisticalAreaSchema>

export const UnsavedLocationSchema = z.object({
  ...AddressSchema.partial().shape,
  ...AddressSchema.pick({ countrySubd: true, line1: true, locality: true, postal1: true }).shape,
  latlng: LatLngSchema,
})
export type UnsavedLocation = z.infer<typeof UnsavedLocationSchema>
/**
 * This is a combination of global, company-specific, and user-specific data
 * for a single location or property
 */

export const PropertyParcelSchema = z.discriminatedUnion('type', [GeoJSONPolygonSchema, GeoJSONMultiPolygonSchema])
export type PropertyParcel = z.infer<typeof PropertyParcelSchema>

export const PinPlacementSourceSchema = z.enum(['first_american', 'amazon_location', 'supplied_coordinates', 'unknown'])
export const PinPlacementPrecisionSchema = z.enum(['house', 'street', 'zip', 'custom_coordinates', 'unknown'])
export const PinPlacementSchema = z.object({
  source: PinPlacementSourceSchema.meta({
    description: 'Data source used to place the account pin; unknown for pins created before placement tracking',
  }),
  precision: PinPlacementPrecisionSchema.meta({
    description: 'Geographic precision of the account pin; unknown for pins created before placement tracking',
  }),
})
export type PinPlacement = z.infer<typeof PinPlacementSchema>
export const UNKNOWN_PIN_PLACEMENT: PinPlacement = { source: 'unknown', precision: 'unknown' }

export const LocationDataSchema = z.object({
  ...AddressSchema.shape,
  locationId: LocationIdSchema,
  altId: z.string().optional(),
  addressMasterId: z
    .number()
    .optional()
    .meta({ description: 'Numeric ID of the matching property data record, when known' }),
  latlng: LatLngSchema,
  altLatLngs: AltLatLngSchema.array().optional(),
  fields: z.array(FieldDataSchema).optional(),
  attributes: LocationAttributesSchema.optional(),
  residents: z.array(ResidentDataSchema).optional(),
  titleOwners: z.array(ResidentDataSchema).optional(),
  msa: MetropolitanStatisticalAreaSchema.optional(),
  cbsa: MetropolitanStatisticalAreaSchema.optional(),
  propertyTypeId: z.union([z.string(), z.number()]).optional(),
  notes: z.array(NoteWithOwnerSchema).optional(),
  disposition: DispositionIdSchema.optional(),
  addressParts: FirstAmericanStreetSchema.optional(),
  parcel: PropertyParcelSchema.optional(),
})
export type LocationData = z.infer<typeof LocationDataSchema>

export const LocationCustomDataSchema = z.object({
  ...LocationDataSchema.omit({ locationId: true }).shape,
  locationId: CustomLocationIdSchema,
})
export type LocationCustomData = z.infer<typeof LocationCustomDataSchema>

export type TinyLocationData = TinyAddress &
  LatLng & {
    readonly locationId: LocationId
    accountContact?: TinyAccountContact
    /** @deprecated use residents */
    resident?: TinyResidentData
    residents?: TinyResidentData[]
    titleOwners?: ResidentData[]
    notes?: NoteWithOwner[]
  }

export type PropertyType = number | string

/** WARNING - Changing this value requires clearing the Geo cache from DynamoDB */
export const CELL_SIZE = 0.005

export const ACRE = 43560

/** Geo200:Longitude,Latitude or Geo200:x,y */
export const Geo200IdSchema = z.templateLiteral([
  z.literal('Geo200:'),
  z.number().describe('longitude'),
  z.literal(','),
  z.number().describe('latitude'),
])
export type Geo200Id = z.infer<typeof Geo200IdSchema>
export const Geo200CompanySchema = z.templateLiteral([Geo200IdSchema, z.literal('|'), CompanyIdSchema])
export type Geo200Company = z.infer<typeof Geo200CompanySchema>

export function isGeo200Id(value: unknown): value is Geo200Id {
  if (typeof value !== 'string') return false
  return value.startsWith('Geo200:')
}

export type GeoIdParts = LatLng & {
  cellSize: number
}

export type WithLocationId = {
  locationId: LocationId
}

export type WithOptionalLocationId = Partial<WithLocationId>

export const SmallLocationDataSchema = LocationDataSchema.pick({
  locationId: true,
  addressMasterId: true,
  latlng: true,
  line1: true,
  line2: true,
  countrySubd: true,
  locality: true,
  postal1: true,
  unitNbr: true,
  unitType: true,
  residents: true,
  addressParts: true,
  altLatLngs: true,
  titleOwners: true,
})

export type SmallLocationData = z.infer<typeof SmallLocationDataSchema>

export type LocationMatchReport = {
  /** The smaller the number, the closer match it is (0 is the closest) */
  closeness: number
  haversineDistance?: number
}

export type ExtendedLocationMatchReport = LocationMatchReport & {
  locationId: LocationId
  line1: string
  latlng: LatLng
  isHighConfidenceMatch?: boolean
  isMediumConfidenceMatch?: boolean
}

export type GeoHashData = {
  geohash: string
  count: number
  coordinates: LatLng[]
  center: LatLng
}
export type LocationActivityHeatMapCell = GeoHashData & {
  statuses?: Partial<Record<AccountStatusId, number>>
  dispositions?: Partial<Record<DispositionId, number>>
}

export type LocationActivityResponse = LocationActivityHeatMap | LocationActivityPins
type LocationActivityHeatMap = { mode: 'heatmap'; grid: LocationActivityHeatMapCell[] }
type LocationActivityPins = { mode: 'pin'; activity: ActivityData[] }

export type LocationActivityId = AccountStatusId | DispositionId | WorkflowActionId | WorkflowStageId
export type ActivityData = {
  activityId: LocationActivityId
  actionId?: WorkflowActionId | TagId
  latLng: LatLng
  locationId: LocationId
  user: TinyUser | UserId
  timestamp: number
}

export const OccupantTypeSchema = z.enum(['Renter', 'Owner'])
export type LocationOccupantType = z.infer<typeof OccupantTypeSchema>

export const LocationMapPermitFilterSchema = z.object({
  label: PermitLabelSchema,
  status: PermitStatusSchema.array().optional(),
  statusDate: RangeFilterSchema.optional(),
  installers: z.array(z.string()).optional(),
})
export type LocationMapPermitFilter = z.infer<typeof LocationMapPermitFilterSchema>

export const FilterClauseSchema = z.enum(['and', 'or', 'none'])
export type FilterClause = z.infer<typeof FilterClauseSchema>

const LocationMapPermitClauseFilterSchema = z.object({
  clause: FilterClauseSchema,
  filters: LocationMapPermitFilterSchema.array(),
})
export type LocationMapPermitClauseFilter = z.infer<typeof LocationMapPermitClauseFilterSchema>

const PoolFilterSchema = z.object({
  type: z.literal('pool'),
  value: z.boolean(),
})
export type PoolFilter = z.infer<typeof PoolFilterSchema>

const YearBuiltFilterSchema = z.object({
  type: z.literal('year_built'),
  ...RangeFilterSchema.shape,
})
export type YearBuiltFilter = z.infer<typeof YearBuiltFilterSchema>

const LotSizeFilterSchema = z.object({
  type: z.literal('lot_size'),
  ...RangeFilterSchema.shape,
})
export type LotSizeFilter = z.infer<typeof LotSizeFilterSchema>

const HomeSizeFilterSchema = z.object({
  type: z.literal('home_size'),
  ...RangeFilterSchema.shape,
})
export type HomeSizeFilter = z.infer<typeof HomeSizeFilterSchema>

const OccupantTypeFilterSchema = z.object({
  type: z.literal('occupant_type'),
  value: z.enum(['owner', 'renter']),
})
export type OccupantTypeFilter = z.infer<typeof OccupantTypeFilterSchema>

const OwnershipTransferDateFilterSchema = z.object({
  type: z.literal('ownership_transfer_date'),
  ...RangeFilterSchema.shape,
})
export type OwnershipTransferDateFilter = z.infer<typeof OwnershipTransferDateFilterSchema>

const ResidentAgeFilterSchema = z.object({
  type: z.literal('resident_age'),
  ...RangeFilterSchema.shape,
})
export type ResidentAgeFilter = z.infer<typeof ResidentAgeFilterSchema>

const ResidentCreditScoreFilterSchema = z.object({
  type: z.literal('resident_credit_score'),
  ...RangeFilterSchema.shape,
})
export type ResidentCreditScoreFilter = z.infer<typeof ResidentCreditScoreFilterSchema>

const ResidentFilterSchema = z.object({
  type: z.literal('resident'),
  age: RangeFilterSchema.optional(),
  creditScore: RangeFilterSchema.optional(),
})
export type ResidentFilter = z.infer<typeof ResidentFilterSchema>

const PermitFilterSchema = z.object({
  type: z.literal('permit'),
  ...LocationMapPermitFilterSchema.shape,
})
export type PermitFilter = z.infer<typeof PermitFilterSchema>

const LienFinancierFilterSchema = z.object({
  type: z.literal('lien_financier'),
  financiers: z.string().array().optional(),
  recordingDate: RangeFilterSchema.optional(),
})
export type LienFinancierFilter = z.infer<typeof LienFinancierFilterSchema>

const LienCountFilterSchema = z.object({
  type: z.literal('lien_count'),
  count: z.number().optional(),
  ...RangeFilterSchema.shape,
})
export type LienCountFilter = z.infer<typeof LienCountFilterSchema>

const ZipCodeFilterSchema = z.object({
  type: z.literal('postal'),
  postal: z.string(),
})

export const LocationMapFilterSchema = z.discriminatedUnion('type', [
  PoolFilterSchema,
  YearBuiltFilterSchema,
  LotSizeFilterSchema,
  HomeSizeFilterSchema,
  OccupantTypeFilterSchema,
  OwnershipTransferDateFilterSchema,
  ResidentAgeFilterSchema,
  ResidentCreditScoreFilterSchema,
  ResidentFilterSchema,
  PermitFilterSchema,
  LienFinancierFilterSchema,
  LienCountFilterSchema,
  ZipCodeFilterSchema,
])
export type LocationMapFilter = z.infer<typeof LocationMapFilterSchema>

export type LocationMapCompoundFilter = {
  must?: LocationMapMustFilter[]
  mustNot?: LocationMapMustFilter[]
}
export type LocationMapShouldFilter = {
  should: (LocationMapFilter | LocationMapCompoundFilter)[]
  minimumShouldMatch?: number
}
export type LocationMapMustFilter = LocationMapFilter | LocationMapShouldFilter

const LocationMapCompoundFilterSchema: z.ZodType<LocationMapCompoundFilter> = z
  .object({
    must: z.lazy(() => LocationMapMustFilterSchema.array()).optional(),
    mustNot: z.lazy(() => LocationMapMustFilterSchema.array()).optional(),
  })
  .refine(({ must, mustNot }) => (must?.length ?? 0) + (mustNot?.length ?? 0) > 0)

export const LocationMapShouldFilterSchema: z.ZodType<LocationMapShouldFilter> = z.object({
  should: z.union([LocationMapFilterSchema, LocationMapCompoundFilterSchema]).array(),
  minimumShouldMatch: z.number().optional().describe('Defaults to 1 when omitted.'),
})

export const LocationMapMustFilterSchema: z.ZodType<LocationMapMustFilter> = z.union([
  LocationMapFilterSchema,
  LocationMapShouldFilterSchema,
])

/**
 * @example
 * (YEAR_BUILT between 1990 and 2020) AND (OCCUPANT_TYPE is owner) AND
 * ((POOL is true) OR ((LOT_SIZE >= 5000) AND (HOME_SIZE >= 2000)))
 * {
 *   must: [
 *     {
 *       type: 'year_built',
 *       gte: 1990,
 *       lte: 2020,
 *     },
 *     {
 *       type: 'occupant_type',
 *       value: 'owner',
 *     },
 *     {
 *       should: [
 *         {
 *           type: 'pool',
 *           value: true,
 *         },
 *         {
 *           must: [
 *             {
 *               type: 'lot_size',
 *               gte: 5000,
 *             },
 *             {
 *               type: 'home_size',
 *               gte: 2000,
 *             },
 *           ],
 *         },
 *       ],
 *       minimumShouldMatch: 1,
 *     },
 *   ],
 * }
 */
export const LocationAttomFilterSchema = z.object({
  pool: z.boolean().optional().meta({ deprecated: true }),
  trust: z.boolean().optional().meta({ deprecated: true }),
  occupantType: OccupantTypeSchema.optional().meta({ deprecated: true }),
  yearBuilt: RangeFilterSchema.optional().meta({ deprecated: true }),
  homeSize: RangeFilterSchema.optional().meta({ deprecated: true }),
  lotSize: RangeFilterSchema.optional().meta({ deprecated: true }),
  parkingSpaces: RangeFilterSchema.optional().meta({ deprecated: true }),
  ownershipTransferDate: RangeFilterSchema.optional().meta({ deprecated: true }),
  residentAge: RangeFilterSchema.optional().meta({ deprecated: true }),
  residentCreditScore: RangeFilterSchema.optional().meta({ deprecated: true }),
  permits: LocationMapPermitClauseFilterSchema.array().optional().meta({ deprecated: true }),
  financiers: z.array(z.string()).optional().meta({ deprecated: true }),
  musts: LocationMapMustFilterSchema.array().optional(),
  mustNots: LocationMapMustFilterSchema.array().optional(),
})
export type LocationAttomFilter = z.infer<typeof LocationAttomFilterSchema>

export const LocationActivityFilterSchema = z.object({
  statusIds: AccountStatusIdSchema.array().optional(),
  workflowId: WorkflowIdSchema.optional(),
  workflowStageIds: WorkflowStageIdSchema.array().optional(),
  workflowActionIds: WorkflowActionIdSchema.array().optional(),
  tagIds: z.array(TagIdSchema).optional(),
  dispositionIds: DispositionIdSchema.array().optional(),
  pitch: z.boolean().optional(),
  startTime: UTCMillisecondsSchema.optional(),
  endTime: UTCMillisecondsSchema.optional(),
  userIds: UserIdSchema.array().optional(),
  customFields: CustomFieldFilterSchema.optional(),
})
export type LocationActivityFilter = z.infer<typeof LocationActivityFilterSchema>

export type LocationUserActivity = { user: TinyUser; latLng: LatLng; timestamp: number }

export type LocationMapBoundingBox = LatLngBoundingBox

export type LocationLastActivityFilter = {
  teamIds?: TeamId[]
  boundingBox?: LocationMapBoundingBox
}

const WithActivityDateSchema = z.object({
  date: z.number(),
})
export type WithActivityDate = z.infer<typeof WithActivityDateSchema>

export const LocationAuditSchema = z.object({
  userId: UserIdSchema,
  accounts: z.number(),
  contacts: z.number(),
  events: z.number(),
  prescreens: z.number().optional(),
  summary: z.object({
    accounts: WithActivityDateSchema.array(),
    contacts: WithActivityDateSchema.array(),
    events: WithActivityDateSchema.array(),
    prescreens: WithActivityDateSchema.array().optional(),
  }),
})
export type LocationAudit = z.infer<typeof LocationAuditSchema>

export const WeatherTypeSchema = z.enum(['hail', 'wind'])
export type WeatherType = z.infer<typeof WeatherTypeSchema>

export const WeatherGeometrySchema = z
  .object({
    polygons: z.object({
      type: z.literal('MultiPolygon'),
      coordinates: z.array(z.array(z.array(z.array(z.number())))),
    }),
    hailSize: z.string().optional(),
    maxGust: z.string().optional(),
  })
  .array()
export type WeatherGeometry = z.infer<typeof WeatherGeometrySchema>

/** One pre-built weather overlay file: the merged shape of one level (gust speed or hail size) on one day */
export const WeatherOverlayFileSchema = z.object({
  level: z.string().meta({ description: 'Gust speed in mph (wind) or hail size in inches (hail)' }),
  sizeBytes: z.number().meta({ description: 'Compressed size of the file' }),
  url: z.string().meta({
    description:
      'Signed S3 link to a JSON object `{ polygons: MultiPolygon, maxGust?: number, hailSize?: number }`. ' +
      'Served with `Content-Encoding: gzip`: HTTP clients unzip it, so read the body as plain JSON.',
  }),
})
export type WeatherOverlayFile = z.infer<typeof WeatherOverlayFileSchema>

export const WeatherOverlayDaySchema = z.object({
  date: z.iso.date(),
  status: z.enum(['ready', 'pending']).meta({ description: 'pending: this day is not built yet' }),
  builtAt: z.iso.datetime().optional().meta({ description: 'Changes when the day is rebuilt' }),
  files: WeatherOverlayFileSchema.array(),
})
export type WeatherOverlayDay = z.infer<typeof WeatherOverlayDaySchema>
